import React from 'react';
import { useIntl } from 'react-intl';
import type { ViewPreset } from '../cameraRig';
import type { DisplayMode } from '../stage/displayModes';
import type { CapturedView, Environment, EnvironmentToggle, SelectionMode } from '../viewerSettings';
import type { CubeLayout, SectionSettings, UnderLightSettings } from '../viewport';
import type { ViewRenderCapability } from '../viewRender';
import AiRender from './ai/AiRender';
import EnvironmentBar from './EnvironmentBar';
import ExpandButton from './ExpandButton';
import GridMenu from './GridMenu';
import { messages } from './messages';
import NavigationHelp from './NavigationHelp';
import SectionBar from './SectionBar';
import ToolRail from './ToolRail';
import TopBar from './TopBar';
import ViewCubeControls from './ViewCubeControls';

export interface ViewerChromeProps {
  isDark: boolean;
  hasModel: boolean;
  narrow: boolean;
  showGridReadout: boolean;
  /** Why picking and measuring are off for the model on screen, when they are. */
  pickUnavailable?: string;
  displayMode: DisplayMode;
  onDisplayModeChange: (mode: DisplayMode) => void;
  selectionMode: SelectionMode;
  onSelectionModeChange: (mode: SelectionMode) => void;
  section: SectionSettings | null;
  sectionOffset: number;
  onToggleSection: () => void;
  onSectionChange: (section: SectionSettings | null) => void;
  onSectionOffsetChange: (offset: number) => void;
  attachSectionBar: (element: HTMLDivElement | null) => void;
  measuring: boolean;
  onToggleMeasure: () => void;
  dimensions: boolean;
  onToggleDimensions: () => void;
  environment: Environment;
  onToggleEnvironment: (key: EnvironmentToggle) => void;
  underLight: UnderLightSettings;
  onUnderLightChange: (settings: UnderLightSettings) => void;
  backgroundOn: boolean;
  backgroundError?: string;
  onBackgroundFile: (file: File) => void;
  onBackgroundRemove: () => void;
  gridSpacing: number | null;
  fixedGridSpacing: number | null;
  onGridSpacingChange: (spacing: number | null) => void;
  cubeLayout: CubeLayout;
  cubeHovered: boolean;
  onResetView: () => void;
  onPreset: (preset: ViewPreset) => void;
  onFitView: () => void;
  onExpand?: () => void;
  viewRender?: ViewRenderCapability;
  captureView: (maxEdge: number) => CapturedView | null;
  onRenderResult: (image: string) => void;
}

/** Everything that floats over the scene of a full-size viewer. */
const ViewerChrome: React.FC<ViewerChromeProps> = (props) => {
  const intl = useIntl();
  const loadFirst = props.hasModel ? undefined : intl.formatMessage(messages.loadModelFirst);
  return (
    <>
      <NavigationHelp />
      <TopBar
        displayMode={props.displayMode}
        onDisplayModeChange={props.onDisplayModeChange}
        selectionMode={props.selectionMode}
        onSelectionModeChange={props.onSelectionModeChange}
        selectionUnavailable={loadFirst ?? props.pickUnavailable}
        sectionUnavailable={loadFirst}
        sectionOn={props.section !== null}
        onToggleSection={props.onToggleSection}
      />
      {props.hasModel && (
        <ToolRail
          measuring={props.measuring}
          onToggleMeasure={props.onToggleMeasure}
          measureUnavailable={props.pickUnavailable}
          dimensions={props.dimensions}
          onToggleDimensions={props.onToggleDimensions}
        />
      )}
      {props.section && (
        <SectionBar
          ref={props.narrow ? undefined : props.attachSectionBar}
          section={props.section}
          isDark={props.isDark}
          offset={props.sectionOffset}
          onChange={props.onSectionChange}
          onOffsetChange={props.onSectionOffsetChange}
          onExit={() => props.onSectionChange(null)}
          docked={props.narrow}
        />
      )}
      <ViewCubeControls
        layout={props.cubeLayout}
        cubeHovered={props.cubeHovered}
        onResetView={props.onResetView}
        onPreset={props.onPreset}
      />
      {props.onExpand && <ExpandButton onExpand={props.onExpand} />}
      {props.viewRender && props.hasModel && (
        <AiRender
          capability={props.viewRender}
          captureView={props.captureView}
          onResult={props.onRenderResult}
        />
      )}
      <EnvironmentBar
        environment={props.environment}
        onToggle={props.onToggleEnvironment}
        underLight={props.underLight}
        onUnderLightChange={props.onUnderLightChange}
        backgroundOn={props.backgroundOn}
        backgroundError={props.backgroundError}
        onBackgroundFile={props.onBackgroundFile}
        onBackgroundRemove={props.onBackgroundRemove}
        onFitView={props.onFitView}
      />
      {props.showGridReadout && props.gridSpacing !== null && (
        <GridMenu spacing={props.gridSpacing} fixed={props.fixedGridSpacing} onChange={props.onGridSpacingChange} />
      )}
    </>
  );
};

export default ViewerChrome;
